/**
 * A styled, promise-based confirmation to replace `window.confirm`.
 *
 * The native confirm is a blocking OS dialog: unstyled, untranslatable in tone,
 * and it freezes the whole tab. This gives the same one-line contract —
 * `if (await confirm({…})) …` — while rendering an in-app modal that wears the
 * console's own frame, danger colour and display face, and that a keyboard user
 * can drive like any other dialog.
 *
 * One host lives at the app root; `useConfirm()` returns the function that opens
 * it and resolves to the operator's answer.
 */
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react'
import { AlertTriangle } from 'lucide-react'
import { Dialog } from './controls'
import { Icon } from './icon'
import { Button } from './primitives'
import { useT } from '@/i18n/context'

export interface ConfirmOptions {
  title: string
  message: ReactNode
  /** Defaults to the generic "confirm"; a destructive action should name itself
   *  (e.g. "Delete") so the button says what it does, not just "OK". */
  confirmLabel?: string
  cancelLabel?: string
  /** `danger` paints the confirm button red and shows the hazard glyph — the
   *  default, because a confirmation an operator is asked for is almost always
   *  guarding something irreversible. */
  tone?: 'danger' | 'default'
}

type ConfirmFn = (opts: ConfirmOptions) => Promise<boolean>

const ConfirmContext = createContext<ConfirmFn | null>(null)

export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [options, setOptions] = useState<ConfirmOptions | null>(null)
  const resolver = useRef<((v: boolean) => void) | null>(null)

  const confirm = useCallback<ConfirmFn>((opts) => {
    setOptions(opts)
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve
    })
  }, [])

  const settle = useCallback((result: boolean) => {
    resolver.current?.(result)
    resolver.current = null
    setOptions(null)
  }, [])

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {options && (
        <ConfirmDialog
          options={options}
          onCancel={() => settle(false)}
          onConfirm={() => settle(true)}
        />
      )}
    </ConfirmContext.Provider>
  )
}

export function useConfirm(): ConfirmFn {
  const ctx = useContext(ConfirmContext)
  if (!ctx) throw new Error('useConfirm must be used within a ConfirmProvider')
  return ctx
}

function ConfirmDialog({
  options,
  onCancel,
  onConfirm,
}: {
  options: ConfirmOptions
  onCancel: () => void
  onConfirm: () => void
}) {
  const t = useT()
  const { title, message, confirmLabel, cancelLabel, tone = 'danger' } = options
  return (
    <Dialog
      title={title}
      onClose={onCancel}
      footer={
        <>
          <Button variant="default" size="sm" onClick={onCancel}>
            {cancelLabel ?? t('action.cancel')}
          </Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} size="sm" onClick={onConfirm}>
            {confirmLabel ?? t('action.confirm')}
          </Button>
        </>
      }
    >
      <div className={`k-confirm k-confirm--${tone}`}>
        {tone === 'danger' && <Icon icon={AlertTriangle} size={20} className="k-confirm__icon" />}
        <p className="k-confirm__msg">{message}</p>
      </div>
    </Dialog>
  )
}
